import { Box, Stack } from "@mui/material";
import VirtualizedList, { VirtualizedListItem } from "../virtualized-list/VirtualizedList";
import { useFieldArray, useFormContext } from "react-hook-form";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { useTranslation } from "react-i18next";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import NoData from "../no-data/NoData";
import { RecipientTypeEnum } from "../../common/enums/RecipientTypeEnum";
import { Ref, forwardRef, useImperativeHandle } from "react";
import { cloneDeep } from "lodash";
import { BaseActions } from "../action-menu/ActionMenuItem";
import { FormRecipientActions, IFormRecipient, IFormUserRecipient, RecipientsFormType } from "./FormRecipientListUtils";
import { RecipientSubTypeEnum } from "../../common/enums/RecipientSubTypeEnum";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useGroup } from "../../queries/groups/UseGroup";
import { useSaveRecipient } from "../../queries/recipients/UseSaveRecipient";
import { IRecipientEditDTO } from "../../services/recipient-data-service/Models";
import AlarmSetpointNotificationUtils from "../alarm-setpoint-editor/alarm-setpoint-notifications/AlarmSetpointNotificationUtils";
import RecipientsEditorDialog, { useRecipientsEditorDialogState, bindRecipientsEditorDialog } from "../alarm-setpoint-editor/recipients-editor/RecipientsEditorDialog";
import RecipientEditDialog, { useRecipientEditDialogState, OpenMode, bindRecipientEditDialog } from "../recipient-edit-dialog/RecipientEditDialog";
import { FormRecipientListItem } from "./FormRecipientListItem";
import { contains } from "../../common/utilities/Contains";


export type FormRecipientItem = IFormUserRecipient & VirtualizedListItem; 

export type FormRecipientListProps = {
    visible?: boolean;
    viewSelected: boolean;
    filterText: string;
    recipientType: RecipientTypeEnum,
    organizationID: number | undefined,
    allowVoiceAlarm: boolean,
    onRecipientSelected?: (value: boolean) => void;
    hideActions?: boolean;
};

export type FormRecipientListType = {
    executeAction: (action: FormRecipientActions | BaseActions) => void;
};


function FormRecipientList({
    visible,
    viewSelected,
    filterText,
    recipientType,
    organizationID,
    allowVoiceAlarm,
    onRecipientSelected,
    hideActions
}: FormRecipientListProps, ref: Ref<unknown> | undefined) {

    const {t} = useTranslation();
    const { acknowledge } = useConfirmation();
    const [userState] = useUserContext();
    const routeContext = useRouteContext();
    
    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number
    });

    
    const { mutateAsync: saveRecipient } = useQueryNotifications(useSaveRecipient)({
        groupID: routeContext.groupID as number,
        organizationID: organizationID ?? group?.organizationID as number,
        onSuccess: (data) => {
            // save success            
            acknowledge(t("saveSuccessMessage", {
                itemName: getObjectTypeName(eLynxObjectTypeEnum.Recipient)
            }));
        }
    });


    useImperativeHandle<unknown, FormRecipientListType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const {
        watch,
        control,
    } = useFormContext<RecipientsFormType>();    

    const {
        fields,
        append,
        //remove:
        update
    } = useFieldArray({
        control: control,
        name: 'userRecipients',
        keyName: "fieldKey"
    });    

    const watchRecipients = watch("userRecipients");

    const userRecipients =
        fields
        .sort((a, b) => a.userName.toLowerCase().localeCompare(b.userName.toLowerCase()))
        .map((field, index) => {
            return {
                ...field,
                ...watchRecipients[index],
                fieldKey: `userRecipients.${index}`
            };
        });


    const count = AlarmSetpointNotificationUtils.filterRecipients(userRecipients, filterText, recipientType, organizationID).length;

    const recipientEditDialog = useRecipientEditDialogState();
    const recipientsEditorDialog = useRecipientsEditorDialogState();

    function executeAction(action: FormRecipientActions | BaseActions) {

        switch(action) {
            case FormRecipientActions.Add:
                onAddRecipient();
                break;
        }
    }

    async function onAddRecipient(item?: IFormUserRecipient) {

        const result = await recipientEditDialog.open({
            openMode: OpenMode.Add,
            organizationID: organizationID ?? group?.organizationID,
            recipientID: -1,
            recipientType:  RecipientTypeEnum.Email,
            recipientSubType: RecipientSubTypeEnum.None,
            recipientTo: '',
            allowCloneRecipientUsage: false,
            allowLinkToRecipientUser: item === undefined,
            userID: item?.userID
        });

        if (result.reason === "ok") {

            const formValues = result.values;

            const userID = item?.userID ?? formValues?.userID ?? -1;
            if (formValues) {

                let model: IRecipientEditDTO = {
                    recipientID: formValues.recipientID,
                    recipientType: formValues.recipientType,
                    recipientSubType: formValues.recipientSubType,
                    recipientTo: formValues.recipientTo,
                    userID: userID,
                    userFirstName: formValues.userFirstName ?? '',
                    userLastName: formValues.userLastName ?? ''
                }

                model = await saveRecipient(model);

                // recipient was linked to existing recipient user
                if (userID > 0) {

                    const userRecipient = userRecipients.find(o => o.userID === userID);
                    const ndx = userRecipients.findIndex(o => o.userID === userID);

                    const newRecipient: IFormRecipient = {

                        id: model.recipientID,
                        recipientType: model.recipientType,
                        recipientSubType: model.recipientSubType,
                        to: formValues.recipientTo,
                        organizationID: organizationID ?? group?.organizationID as number
                    };


                    const updateItem = {...userRecipient, 
                        recipients: [...(userRecipient?.recipients ?? []), newRecipient ]
                    } as IFormUserRecipient;

                    update(ndx, updateItem);

                } else {

                    // create an adhoc user and new recipient
                    const newUserRecipient: IFormUserRecipient = {
                        userID: model.userID,
                        userName: `${model.userLastName}, ${model.userFirstName}`,
                        recipients: [{
                            id: model.recipientID,
                            recipientType: model.recipientType,
                            recipientSubType: model.recipientSubType,
                            to: model.recipientTo as string,
                            organizationID: organizationID ?? group?.organizationID as number
                        } as IFormRecipient]
                    }

                    append(newUserRecipient);
                }
            }
        }
    }

    async function onEditRecipient(item: IFormUserRecipient) {

        const result = await recipientsEditorDialog.open({
            recipients: cloneDeep(item.recipients),
            organizationID: organizationID ?? group?.organizationID as number
        });

        if (result.reason === 'ok') {

            const values = result.values?.recipients;
            
            if (values) {
            
                const userRecipient = userRecipients.find(o => o.userID === item.userID);
                const ndx = userRecipients.findIndex(o => o.userID === item.userID);

                const updateItem = {...userRecipient, 
                    recipients: [...(userRecipient?.recipients ?? [])]
                } as IFormUserRecipient;


                values.forEach(o => {

                    const recipient = updateItem?.recipients.find(v => v.id === o.recipientID);

                    if (recipient) {
                        recipient.to = o.recipientTo as string;
                    }
                });
                

                update(ndx, updateItem);
            }
        }
    }

    function onExecuteAction(action: FormRecipientActions, payload: IFormUserRecipient) {
        switch(action) {

            case FormRecipientActions.Add:
                onAddRecipient(payload);
                break;

            case FormRecipientActions.Edit:
                onEditRecipient(payload);

            break;
        }
    }

    function renderItem(_index: number, item: IFormUserRecipient) {
        return (
            <FormRecipientListItem
                item={item}
                key={`${item.userID}`}
                //filter={filterText}
                showSelected={viewSelected}
                recipientType={recipientType}
                allowVoiceAlarm={allowVoiceAlarm}
                onExecuteAction={onExecuteAction}
                onSelected={onRecipientSelected}
                hideActions={hideActions}
            />
        );
    };


    function onFilterItem(item: FormRecipientItem): boolean {

        let show = !!contains(item.userName, filterText) || item.recipients.find(o => !!contains(o.to, filterText)) !== undefined;

        if (show && recipientType !== RecipientTypeEnum.Invalid /* ALL */) {
            show = item.recipients.filter(o => o.recipientType === recipientType).length > 0;
        }

        if (show && organizationID) {
            show = item.recipients.filter(o => o.organizationID === organizationID).length > 0;
        }

        if (show && viewSelected) {
            show = item.recipients.filter(o => o.selected).length > 0;
        }

        return show;
    }

    return (visible ? <Stack
        direction='row'
        sx={{
            display: 'flex',
            flex: '1 1 auto'
        }}>
        <Box
            sx={{
                display: 'flex',
                flex: '1 1 auto',
                flexDirection: 'column',
                overflowY: 'auto'
            }}>
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                overflow: "hidden"
                }}>

                {count === 0 &&         
                    <Box sx={{
                        padding: 1
                    }}>
                        <NoData text={t('setpointAdmin.nodata.noFilterMatch', {name: getObjectTypeName(eLynxObjectTypeEnum.Recipient, true, true)})} />
                    </Box>
                }
                                        
                <VirtualizedList
                    items={userRecipients as FormRecipientItem[]}
                    renderItem={renderItem}
                    filter={onFilterItem}
                />

            </Box>
        </Box>


        <RecipientEditDialog {...bindRecipientEditDialog(recipientEditDialog)} />
        <RecipientsEditorDialog {...bindRecipientsEditorDialog(recipientsEditorDialog)} />
    </Stack> : null);
}

export default forwardRef(FormRecipientList);